<template>
	<div class="h-full">
		<div class="w-full mb-5 flex justify-between">
			<h1 class="text-3xl font-black text-dbBlue">
				Invoices
			</h1>
		</div>
		<div class="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
			<div class="card col-span-2">
				<div class="flex flex-wrap items-center justify-between sm:flex-nowrap px-4 py-[14px] sm:px-6 bg-white relative overflow-hidden">
					<div>
						<h2 class="card-heading">
							All Invoices
						</h2>
					</div>
					<div class="flex-shrink-0">
						<a
							href="/DBServices-W9-2026.pdf"
							target="_blank">
							<tw-button class="btn-base btn-indigo w-44">
								<template #icon_right>
									<ArrowDownTrayIcon class="w-5 h-5" />
								</template>
								Download our W-9
							</tw-button>
						</a>
					</div>
					<card-header-backdrop class="opacity-20" />
				</div>
				<div class="px-4 py-5 sm:p-6">
					<tw-table
						:data="invoices"
						:loading="loading"
						placeholder="No Invoices">
						<tw-table-column
							condensed
							header="Invoice #" 
							prop="invoiceNumber"
							class-name="font-bold text-gray-900">
							<template #default="{row}">
								{{ row.invoiceNumber }}
								<div class="md:hidden !font-normal mt-4">
									<tw-badge-flat
										v-if="showStatusBadge(row)"
										:color="invoiceStatusColor(row)">
										{{ invoiceStatusLabel(row) }}
									</tw-badge-flat>
									<span v-else>
										{{ row.paymentTerms }}
									</span>
								</div>
							</template>
						</tw-table-column>
						<tw-table-column
							condensed
							header="Amount Due">
							<template #default="{row}">
								<span :class="{'text-red-600': row.isOverdue, 'text-yellow-600' : !row.isOverdue && row.status === 'Invoiced'}">
									{{ currencyFormatter.format(amountDueWithoutProcessingFee(row)) }}
								</span>
								<div class="md:hidden mt-4">
									<span v-if="!isDueOnReceipt(row.paymentTerms)">
										{{ row.dueDate }}
									</span>
									<span v-else>
										{{ row.paymentTerms }}
									</span>
								</div>
							</template>
						</tw-table-column>
						<tw-table-column
							condensed
							class-name="hidden md:table-cell"
							header-class-name="hidden md:table-cell"
							header="Invoice Date" 
							prop="invoiceDate" />
						<tw-table-column
							header="Due Date" 
							header-class-name="hidden md:table-cell"
							class-name="hidden md:table-cell"
							prop="dueDate">
							<template #default="{row}">
								<span v-if="!isDueOnReceipt(row.paymentTerms)">
									{{ row.dueDate }}
								</span>
								<span v-else>
									{{ row.paymentTerms }}
								</span>
							</template>
						</tw-table-column>
						<tw-table-column
							class-name="hidden md:block"
							header-class-name="hidden md:block"
							header="Status" 
							prop="status">
							<template #default="{row}">
								<tw-badge-flat
									v-if="showStatusBadge(row)"
									:color="invoiceStatusColor(row)">
									{{ invoiceStatusLabel(row) }}
								</tw-badge-flat>
								<span v-else>
									{{ row.paymentTerms }}
								</span>
							</template>
						</tw-table-column>
						<tw-table-column
							condensed
							class-name="hidden md:table-cell w-44"
							header-class-name="hidden md:table-cell sr-only w-44"
							header="Pay">
							<template #default="{ row }">
								<pay-button
									v-if="isPayable(row) && !loading"
									:invoice="row"
									@submitted="getInvoices"
									@verification-canceled="clearPendingVerification(row)" />
							</template>
						</tw-table-column>
						<tw-table-column
							condensed
							header="View Details" 
							header-class-name="sr-only"
							class-name="text-center md:text-right">
							<template #default="{row}">
								<InvoiceDetailsButton :invoice="row" />
								<pay-button
									class="md:hidden mt-4"
									v-if="isPayable(row) && !loading"
									:invoice="row"
									@submitted="getInvoices"
									@verification-canceled="clearPendingVerification(row)" />
							</template>
						</tw-table-column>
					</tw-table>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export default { name: 'InvoicesMain' } // name the component
</script>

<script setup lang="ts">
	// api
	import api from '@/utilities/api'

	import {
		isDueOnReceipt,
		isPayable,
		showStatusBadge,
		invoiceStatusColor,
		invoiceStatusLabel,
		clearPendingVerification,
		applyInvoiceDueStatus,
		amountDueWithoutProcessingFee
	} from '@/utilities/invoiceStatus'

	import {
		Auth
	} from 'aws-amplify'

	// components
	import InvoiceDetailsButton from "./components/InvoiceDetailsButton.vue"
	import PayButton from './components/PayButton.vue'

	import {
		ArrowDownTrayIcon,
	} from "@heroicons/vue/20/solid"

	// vue
	import {
		ref,
		onMounted
	} from "vue"

	const loading = ref(true)
	const invoices = ref<Array<any>>([])

	const currencyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	})

	onMounted(() => {
		getInvoices()
	})

	// pulls the invoice list for the logged in contact out of FileMaker
	async function fetchInvoiceList() {
		const user = await Auth.currentAuthenticatedUser()

		const body = {
			action: "invoiceList",
			payload: {
				id: user.attributes['custom:fm_contact_id'],
				companyID: user.attributes['custom:fm_company_id']
			}
		}
		const response = await api("post", "fmDataAPI", "/fmDataAPI", body)
		const scriptResult = JSON.parse(response.response.scriptResult)

		return scriptResult.result.data
	}

	async function getInvoices() {
		try {
			loading.value = true

			const todayDate = new Date()

			let _invoices = await fetchInvoiceList()

			// filter pending invoices for now
			_invoices = _invoices?.filter((invoice: any) => {
				return invoice.status != "Pending"
			})
			_invoices = _invoices.filter((invoice: any) => {
				return invoice.status != "Cancelled"
			})

			_invoices.forEach((invoice: any) => {
				//  split invoices summaries string
				invoice.summaries = invoice.summaries.split("\r")

				applyInvoiceDueStatus(invoice, todayDate)
			})

			invoices.value = _invoices
		}
		catch(e) {
			console.error(e)
		}
		finally {
			loading.value = false
		}
	}

</script>
